import { useEffect, useRef } from "react"

import {
  listPendingAgentAsks,
  type PendingAgentAsk,
} from "../api/agent-conversation-client"

type PendingAgentAsksResult = {
  conversationId: string
  pendingAsks: PendingAgentAsk[]
  startedMutationVersion: number
}

type UsePendingAgentAsksInput = {
  enabled: boolean
  conversationId: string | null
  mutationVersion: number
  onPendingAsks: (result: PendingAgentAsksResult) => void
}

/**
 * Polls only the selected persisted conversation for backend-owned ask_user
 * cards. Requests never overlap; each tick schedules the next only after the
 * previous one settles, which keeps slow networks from racing local mutations.
 */
export function usePendingAgentAsks({
  enabled,
  conversationId,
  mutationVersion,
  onPendingAsks,
}: UsePendingAgentAsksInput) {
  const callbackRef = useRef(onPendingAsks)
  const mutationVersionRef = useRef(mutationVersion)

  useEffect(() => {
    callbackRef.current = onPendingAsks
  }, [onPendingAsks])

  useEffect(() => {
    mutationVersionRef.current = mutationVersion
  }, [mutationVersion])

  useEffect(() => {
    if (!enabled || !conversationId) return

    let cancelled = false
    let timeout: ReturnType<typeof setTimeout> | null = null
    let controller: AbortController | null = null

    const tick = async () => {
      controller = new AbortController()
      const startedMutationVersion = mutationVersionRef.current
      try {
        const { pendingAsks } = await listPendingAgentAsks(
          conversationId,
          controller.signal,
        )
        if (!cancelled) {
          callbackRef.current({
            conversationId,
            pendingAsks,
            startedMutationVersion,
          })
        }
      } catch (error) {
        if (!cancelled && !(error instanceof DOMException && error.name === "AbortError")) {
          console.warn("[agent-pending-asks] poll failed", error)
        }
      } finally {
        if (!cancelled) timeout = setTimeout(tick, 2000)
      }
    }

    void tick()
    return () => {
      cancelled = true
      controller?.abort()
      if (timeout) clearTimeout(timeout)
    }
  }, [enabled, conversationId])
}
